import { type MenuOption } from '@saviynt/design-system';

import { type AdvancedFilterState, type TabFilterState } from '../types';

import { ADVANCED_FILTERS_METHODS } from './global';

// Initial call constants
export const VIOLATIONS_INI_CALL: Record<string, string> = {
  TAB: 'const_for_tab_count_call',
  ADF: 'const_for_ad_filter_call',
  COP: 'const_for_column_picker_call',
} as const;

// Tab filters, Advanced filters and search
export const TABS_FILTERS: Record<string, TabFilterState> = {
  OPENCOUNT: {
    label: 'Open',
    value: 'openCount',
    count: 0,
    isInitial: false,
    color: 'critical',
  },
  MITIGATEDCOUNT: {
    label: 'Mitigated',
    value: 'mitigatedCount',
    count: 0,
    isZeroBased: true,
    isInitial: false,
    color: 'info',
  },
  INREVIEWCOUNT: {
    label: 'In Review',
    value: 'inReviewCount',
    count: 0,
    isZeroBased: true,
    isInitial: false,
    color: 'warning',
  },
  REMEDIATEDCOUNT: {
    label: 'In Remediation',
    value: 'remediatedCount',
    count: 0,
    isZeroBased: true,
    isInitial: false,
    color: 'neutral',
  },
  CLOSEDCOUNT: {
    label: 'Closed',
    value: 'closedCount',
    count: 0,
    isZeroBased: true,
    isInitial: false,
    color: 'success',
  },
} as const;

export const RENDER_MODES: Record<string, 'graph' | 'list'> = {
  GRAPH: 'graph',
  LIST: 'list',
} as const;

const VIOLATIONS_TAB_LIST_AD_FILTER_CAT: Record<string, string> = {
  VIOLATIONS: 'Risk/Violation',
  RULESETS: 'Rulesets',
  ACCOUNTS: 'Accounts',
  APPLICATIONS: 'Applications',
  USER: 'User',
  FUNCTIONS: 'Functions',
  ENTITLEMENT: 'Entitlement',
  MITICON: 'Mitigating Control (MC)',
} as const;

export const VIOLATIONS_TAB_LIST_AD_FILTER: Record<
  string,
  AdvancedFilterState
> = {
  RISKNAME: {
    label: 'Risk Name',
    value: 'riskName',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  RISKTYPE: {
    label: 'Risk Type',
    value: 'riskType',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'SOD',
        value: 'SOD',
        isChecked: false,
      },
      {
        label: 'Critical Access',
        value: 'CRITICAL_ACCESS',
        isChecked: false,
      },
      {
        label: 'None',
        value: 'NONE',
        isChecked: false,
      },
    ],
  },
  PRIORITY: {
    label: 'Risk Priority',
    value: 'priority',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Very Low',
        value: 'VERY_LOW',
        isChecked: false,
      },
      {
        label: 'Low',
        value: 'LOW',
        isChecked: false,
      },
      {
        label: 'Medium',
        value: 'MEDIUM',
        isChecked: false,
      },
      {
        label: 'High',
        value: 'HIGH',
        isChecked: false,
      },
      {
        label: 'Critical',
        value: 'CRITICAL',
        isChecked: false,
      },
    ],
  },
  ACTUALVSPOTENTIAL: {
    label: 'Actual vs Potential',
    value: 'ACTUALVSPOTENTIAL',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Actual Risk',
        value: 'Actual Risk',
        isChecked: false,
      },
      {
        label: 'Potential Risk',
        value: 'Potential Risk',
        isChecked: false,
      },
      {
        label: 'Not Calculated',
        value: 'Not Calculated',
        isChecked: false,
      },
    ],
  },
  BUSINESSPROCESS: {
    label: 'Business Process',
    value: 'BUSINESSPROCESS',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  // Not supported from BE
  // RISKSCORE: {
  //   label: 'Risk Score',
  //   value: 'RISKSCORE',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
  //   method: ADVANCED_FILTERS_METHODS.CUSTOM,
  //   options: [
  //     {
  //       label: 'Medium (a to b)',
  //       value: '[a,b]',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High (b to c)',
  //       value: '[b,c]',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical (c to d)',
  //       value: '[c,d]',
  //       isChecked: false,
  //     },
  //   ],
  // },
  VIOLATIONAGE: {
    label: 'Violation Age',
    value: 'VIOLATIONAGE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Less than 7 days old',
        value: '[null,7]',
        isChecked: false,
      },
      {
        label: 'Less than 14 days old',
        value: '[null,14]',
        isChecked: false,
      },
      {
        label: 'Less than 30 days old',
        value: '[null,30]',
        isChecked: false,
      },
      {
        label: 'More than 30 days old',
        value: '[30,null]',
        isChecked: false,
      },
    ],
  },
  VIOLATIONORIGIN: {
    label: 'Violation Origin',
    value: 'VIOLATIONORIGIN',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.VIOLATIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Preventative',
        value: 'PREVENTATIVE',
        isChecked: false,
      },
      {
        label: 'Detective',
        value: 'DETECTIVE',
        isChecked: false,
      },
    ],
  },
  RULESET: {
    label: 'Ruleset',
    value: 'RULESET',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.RULESETS,
    method: ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS,
  },
  NAME: {
    label: 'Account',
    value: 'name',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  ACCOUNTTYPE: {
    label: 'Account Type',
    value: 'accounttype',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      { label: 'A', value: 'A', isChecked: false },
      { label: 'Account type1', value: 'Account type1', isChecked: false },
      { label: 'Account type2', value: 'Account type2', isChecked: false },
      { label: 'B', value: 'B', isChecked: false },
      { label: 'C', value: 'C', isChecked: false },
      { label: 'CsnOnly', value: 'CsnOnly', isChecked: false },
      { label: 'FIREFIGHTERID', value: 'FIREFIGHTERID', isChecked: false },
      { label: 'Internal', value: 'Internal', isChecked: false },
      { label: 'L', value: 'L', isChecked: false },
      { label: 'S', value: 'S', isChecked: false },
      { label: 'service', value: 'service', isChecked: false },
      {
        label: 'Service Account',
        value: 'Service Account',
        isChecked: false,
      },
      { label: 'Standard', value: 'Standard', isChecked: false },
      { label: 'TEST', value: 'TEST', isChecked: false },
    ],
  },
  USERLOCK: {
    label: 'Userlock',
    value: 'userlock',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ACCOUNTS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      { label: 'Locked', value: 'lockedState', isChecked: false },
      { label: 'Unlocked', value: 'unlockedState', isChecked: false },
    ],
  },
  APPLICATION: {
    label: 'Application',
    value: 'APPLICATION',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.APPLICATIONS,
    method: ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS,
  },
  USERNAME: {
    label: 'Username',
    value: 'username',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FIRSTNAME: {
    label: 'First Name',
    value: 'firstname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  LASTNAME: {
    label: 'Last Name',
    value: 'lastname',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  SYSTEMUSERNAME: {
    label: 'System Username',
    value: 'systemUserName',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  EMPLOYEEID: {
    label: 'Employee ID',
    value: 'employeeid',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  COSTCENTER: {
    label: 'Cost Center',
    value: 'costcenter',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  JOBCODE: {
    label: 'Job Code',
    value: 'jobCode',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FUNCTION: {
    label: 'Function',
    value: 'FUNCTION',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.FUNCTIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FUNCTIONSTATUS: {
    label: 'Function Status',
    value: 'FUNCTIONSTATUS',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.FUNCTIONS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Active',
        value: 'ACTIVE',
        isChecked: false,
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
        isChecked: false,
      },
    ],
  },
  ENTITLEMENT_VALUE: {
    label: 'Entitlement Value Name',
    value: 'entitlement_value',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  ENTITLEMENTTYPE: {
    label: 'Entitlement Type',
    value: 'entitlementType',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  // Not supported from BE
  // SOXCRITICAL: {
  //   label: 'Sox Critical',
  //   value: 'SOXCRITICAL',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Very Low',
  //       value: 'VERY_LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  // SYSCRITICAL: {
  //   label: 'Sys Critical',
  //   value: 'SYSCRITICAL',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Very Low',
  //       value: 'VERY_LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  // CONFIDENTIALITY: {
  //   label: 'Privileged',
  //   value: 'confidentiality',
  //   category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.ENTITLEMENT,
  //   method: ADVANCED_FILTERS_METHODS.FIXED,
  //   options: [
  //     {
  //       label: 'Very Low',
  //       value: 'VERY_LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Low',
  //       value: 'LOW',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Medium',
  //       value: 'MEDIUM',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'High',
  //       value: 'HIGH',
  //       isChecked: false,
  //     },
  //     {
  //       label: 'Critical',
  //       value: 'CRITICAL',
  //       isChecked: false,
  //     },
  //   ],
  // },
  MITIGATINGCTRLNAME: {
    label: 'Mitigating Control Name',
    value: 'MITIGATINGCTRLNAME',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  MCEXPIRYDATE: {
    label: 'MC Expiry Date',
    value: 'MCEXPIRYDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Next 24 hours',
        value: '[0,1]',
        isChecked: false,
      },
      {
        label: 'Next 7 days',
        value: '[0,7]',
        isChecked: false,
      },
      {
        label: 'Next 14 days',
        value: '[0,14]',
        isChecked: false,
      },
      {
        label: 'Next 30 days',
        value: '[0,30]',
        isChecked: false,
      },
    ],
  },
  MCSTARTDATE: {
    label: 'MC Assigned Start Date',
    value: 'MCSTARTDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Last 24 hours',
        value: '[-1,0]',
        isChecked: false,
      },
      {
        label: 'Last 7 days',
        value: '[-7,0]',
        isChecked: false,
      },
      {
        label: 'Last 14 days',
        value: '[-14,0]',
        isChecked: false,
      },
      {
        label: 'Last 30 days',
        value: '[-30,0]',
        isChecked: false,
      },
    ],
  },
  MCENDDATE: {
    label: 'MC Assigned End Date',
    value: 'MCENDDATE',
    category: VIOLATIONS_TAB_LIST_AD_FILTER_CAT.MITICON,
    method: ADVANCED_FILTERS_METHODS.CUSTOM,
    options: [
      {
        label: 'Next 24 hours',
        value: '[0,1]',
        isChecked: false,
      },
      {
        label: 'Next 7 days',
        value: '[0,7]',
        isChecked: false,
      },
      {
        label: 'Next 14 days',
        value: '[0,14]',
        isChecked: false,
      },
      {
        label: 'Next 30 days',
        value: '[0,30]',
        isChecked: false,
      },
    ],
  },
} as const;

export const DEFAULT_FILTERS: string[] = [
  VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value,
  // Not supported from BE
  // VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.ACTUALVSPOTENTIAL.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.MITIGATINGCTRLNAME.value,
  VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value,
] as const;

export const VIOLATIONS_TAB_LIST_INITIAL_FILTERS: AdvancedFilterState[] =
  Object.values(VIOLATIONS_TAB_LIST_AD_FILTER).map((filter) => {
    const isDefaultFilter = DEFAULT_FILTERS.includes(filter.value);

    return {
      ...filter,
      isDisabled: false,
      isChecked: isDefaultFilter,
    };
  });

// Violations Tab List actions
export const LIST_ACTIONS: Record<string, MenuOption> = {
  MITIGATE: {
    label: 'Mitigate',
    value: 'MITIGATE',
    icon: 'SodMitigate',
  },
  REMOVEMC: {
    label: 'Remove Mitigating Control',
    value: 'REMOVEMC',
    icon: 'Delete',
  },
  ADDMC: {
    label: 'Add Mitigating Control',
    value: 'ADDMC',
    icon: 'Add',
  },
  REMEDIATE: {
    label: 'Remediate',
    value: 'REMEDIATE',
    icon: 'SodRemediate',
  },
  MOVETOINREVIEW: {
    label: 'Move to “In Review”',
    value: 'MOVETOINREVIEW',
    icon: 'Comment',
  },
  GENERATEREPORT: {
    label: 'Generate Report',
    value: 'GENERATEREPORT',
    icon: 'Report',
  },
  VIEWDETAILS: {
    label: 'View Details',
    value: 'VIEWDETAILS',
    icon: 'ArrowRight',
  },
  CHANGEMC: {
    label: 'Change Mitigating Controls',
    value: 'CHANGEMC',
    icon: 'SodMitigate',
  },
  CHNAGEMCENDDATE: {
    label: 'Change Mitigating Control End Date',
    value: 'CHNAGEMCENDDATE',
    icon: 'ExtendEndDate',
  },
  REOPEN: {
    label: 'Re-Open',
    value: 'REOPEN',
    icon: 'SOD',
  },
  ADDREVIEWCOMMENTS: {
    label: 'Add Review Comments',
    value: 'ADDREVIEWCOMMENTS',
    icon: 'Comment',
  },
  CHANGEREVIEWENDDATE: {
    label: 'Change Review End Date',
    value: 'CHANGEREVIEWENDDATE',
    icon: 'ExtendEndDate',
  },
  DIVIDER1: {
    label: 'DIVIDER1',
    value: 'DIVIDER1',
    isDivider: true,
  },
  DIVIDER2: {
    label: 'DIVIDER2',
    value: 'DIVIDER2',
    isDivider: true,
  },
  DIVIDER3: {
    label: 'DIVIDER3',
    value: 'DIVIDER3',
    isDivider: true,
  },
} as const;

export const VIOLATIONS_TAB_LIST_ACTIONS: Record<string, MenuOption[]> = {
  [TABS_FILTERS.OPENCOUNT.value]: [
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.MOVETOINREVIEW,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.MITIGATEDCOUNT.value]: [
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.MOVETOINREVIEW,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.CHNAGEMCENDDATE,
    LIST_ACTIONS.ADDMC,
    LIST_ACTIONS.REMOVEMC,
    LIST_ACTIONS.DIVIDER2,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.INREVIEWCOUNT.value]: [
    LIST_ACTIONS.REMEDIATE,
    LIST_ACTIONS.GENERATEREPORT,
    LIST_ACTIONS.DIVIDER1,
    LIST_ACTIONS.ADDREVIEWCOMMENTS,
    LIST_ACTIONS.CHANGEREVIEWENDDATE,
    LIST_ACTIONS.REOPEN,
    LIST_ACTIONS.DIVIDER2,
    LIST_ACTIONS.VIEWDETAILS,
  ],
  [TABS_FILTERS.REMEDIATEDCOUNT.value]: [
    LIST_ACTIONS.VIEWDETAILS,
    LIST_ACTIONS.GENERATEREPORT,
  ],
  [TABS_FILTERS.CLOSEDCOUNT.value]: [
    LIST_ACTIONS.VIEWDETAILS,
    LIST_ACTIONS.GENERATEREPORT,
  ],
} as const;

// Sort Action
export interface ViolationsListSortFieldState {
  label: string;
  value: string;
  isChecked?: boolean;
  options?: string[];
}

export const VIOLTIONS_LIST_SORT_ACTIONS: Record<string, MenuOption> = {
  NEWTOOLD: {
    label: 'Newest to Oldest',
    value: 'NEWTOOLD',
  },
  OLDTONEW: {
    label: 'Oldest to Newest',
    value: 'OLDTONEW',
  },
  ATOZ: {
    label: 'A to Z',
    value: 'ATOZ',
  },
  ZTOA: {
    label: 'Z to A',
    value: 'ZTOA',
    isChecked: false,
  },
  MOSTCRITCAL: {
    label: 'Most Critical',
    value: 'MOSTCRITCAL',
  },
  LEASTCRITICAL: {
    label: 'Least Critical',
    value: 'LEASTCRITICAL',
  },
} as const;

export const VIOLTIONS_LIST_SORT_FIELDS: Record<
  string,
  ViolationsListSortFieldState
> = {
  RISKNAME: {
    label: VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.label,
    value: 'riskName',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
      VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  // Not supported from BE
  // USERNAME: {
  //   label: VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.label,
  //   value: 'userName',
  //   options: [
  //     VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
  //     VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
  //   ],
  // },
  // [VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value]: {
  //   label: VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.label,
  //   value: 'riskScore',
  //   options: [
  //     VIOLTIONS_LIST_SORT_ACTIONS.MOSTCRITCAL.value,
  //     VIOLTIONS_LIST_SORT_ACTIONS.LEASTCRITICAL.value,
  //   ],
  // },
  VIOLATIONSAGE: {
    label: 'Violation Age',
    value: 'violationAge',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value,
      VIOLTIONS_LIST_SORT_ACTIONS.OLDTONEW.value,
    ],
  },
  ACTUALRISK: {
    label: VIOLATIONS_TAB_LIST_AD_FILTER.ACTUALVSPOTENTIAL.label,
    value: 'actualRisk',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.ATOZ.value,
      VIOLTIONS_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  UPDATEDATE: {
    label: 'Update Date',
    value: 'updateDate',
    options: [
      VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value,
      VIOLTIONS_LIST_SORT_ACTIONS.OLDTONEW.value,
    ],
  },
};

// Side panel stuff
export const VIOLATIONS_LIST_SIDEPANEL_TABS: Record<string, string> = {
  TAB_SUMMARY: 'violations_tab_list_sidepanel_summary_tab',
  TAB_HISTORY: 'violations_tab_list_sidepanel_history_tab',
  TAB_USER: 'violations_tab_list_sidepanel_user_tab',
};

export const VIOLATIONS_TAB_LIST_FOOTER_KIND = {
  FOOTER: 'violations_tab_list_sidepanel_footer',
  LIST: 'violations_tab_list_action',
} as const;

export const AUDIT_TRAIL_MAP = {
  'Moved to In-Review': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Moved to In-Review',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Review Comments Added': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Review Comments Added',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Review End Date Changed': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Review End Date Changed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Requested': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Requested',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Request Approved': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Request Approved',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Task Created': {
    colorTheme: 'neutral',
    icon: 'Comment',
    text: 'Remediation Task Created',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Re-Opened': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Re-Opened',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Found': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Found',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Request Rejected': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Remediation Request Rejected',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Remediation Task Failed': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Remediation Task Failed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Closed': {
    colorTheme: 'negative',
    icon: 'SOD',
    text: 'Violation Closed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Mitigating Control Added': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Mitigating Control Added',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Mitigating Control End Date Changed': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Mitigating Control End Date Changed',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Violation Mitigated': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Violation Mitigated',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved From Status to Close': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved From Status to Close',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved to In Review': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved to In Review',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
  'Moved To In-Remediation': {
    colorTheme: 'negative',
    icon: 'MitigatingControls',
    text: 'Moved To In-Remediation',
    connectorKind: 'past',
    medallionKind: 'filled',
  },
};
